import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { HttpRequestsService } from '../../../../core/services/http-requests/http-requests.service';
import { ApiFeelAdditionalData } from '../models/feel-points.model';
import {
    ApiFeelAvailableCoupons,
    ApiFeelPurchasedCoupons,
    ApiFeelRedeemedCoupon,
} from '../models/points-program/coupons.models';
import {
    ApiFeelAvailableGifts,
    ApiFeelPurchasedGifts,
    ApiExchangedGift,
    ApiRedeemedGift,
    ApiFeelGiftToRedeem,
} from '../models/points-program/gifts.models';
import {
    ApiFeelAvailableVouchers,
    ApiFeelPurchasedVouchers,
    ApiFeelRedeemedVoucher,

} from '../models/points-program/vouchers.models';

@Injectable()
/**
 * Servicio para la interacción de Feel con la API
 */
export class ApiFeelPointsProgramService {
    constructor(private readonly http: HttpClient, private readonly apiRequestService: HttpRequestsService) {}

    /**
     * Función para obtener el listado de cupones listos para ser canjeados
     */
    public getAvailableCoupons(storeId: string, languageId: string): Observable<ApiFeelAvailableCoupons> {
        const slug = `loyalty/store/${storeId}/available-coupons`;
        const data = [
            { key: 'sort', value: 'ASC' },
            { key: 'sortType', value: 'POINTS' },
        ];

        const url = this.apiRequestService.requestUrl(slug, languageId, data, true, '1');

        return this.http.get<ApiFeelAvailableCoupons>(url);
    }

    /**
     * Función para obtener el listado de vales listos para ser canjeados
     */
    public getAvailableVouchers(storeId: string, languageId: string): Observable<ApiFeelAvailableVouchers> {
        const slug = `loyalty/store/${storeId}/available-vouchers`;
        const data = [
            { key: 'sort', value: 'ASC' },
            { key: 'sortType', value: 'POINTS' },
        ];

        const url = this.apiRequestService.requestUrl(slug, languageId, data, true, '1');

        return this.http.get<ApiFeelAvailableVouchers>(url);
    }

    /**
     * Función para obtener el listado de regalos listos para ser canjeados
     */
    public getAvailableGifts(storeId: string, languageId: string): Observable<ApiFeelAvailableGifts> {
        const slug = `loyalty/store/${storeId}/available-gifts`;

        const url = this.apiRequestService.requestUrl(slug, languageId, null, true, '1');

        return this.http.get<ApiFeelAvailableGifts>(url);
    }

    /**
     * Función para obtener el listado de cupones canjeados
     */
    public getPurchasedCoupons(storeId: string, languageId: string): Observable<ApiFeelPurchasedCoupons> {
        const slug = `loyalty/store/${storeId}/coupons`;
        const data = [
            { key: 'sort', value: 'ASC' },
            { key: 'sortType', value: 'POINTS' },
            { key: 'status', value: 'AVAILABLE' },
        ];

        const url = this.apiRequestService.requestUrl(slug, languageId, data, true, '1');

        return this.http.get<ApiFeelPurchasedCoupons>(url);
    }

    /**
     * Función para obtener el listado de vales canjeados
     */
    public getPurchasedVouchers(storeId: string, languageId: string): Observable<ApiFeelPurchasedVouchers> {
        const slug = `loyalty/store/${storeId}/vouchers`;
        const data = [
            { key: 'sort', value: 'ASC' },
            { key: 'sortType', value: 'POINTS' },
            { key: 'status', value: 'AVAILABLE' },
        ];
        const url = this.apiRequestService.requestUrl(slug, languageId, data, true, '1');

        return this.http.get<ApiFeelPurchasedVouchers>(url);
    }

    /**
     * Función para obtener el listado de regalos canjeados
     */
    public getPurchasedGifts(storeId: string, languageId: string): Observable<ApiFeelPurchasedGifts> {
        const slug = `loyalty/store/${storeId}/gifts/orders`;
        const data = [{ key: 'status', value: 'TO_BE_DELIVERED' }];
        const url = this.apiRequestService.requestUrl(slug, languageId, data, true, '1');

        return this.http.get<ApiFeelPurchasedGifts>(url);
    }

    /**
     * Función para canjear cupones
     */
    public redeemCoupons(storeId: string, languageId: string, couponId: string):
    Observable<ApiFeelRedeemedCoupon> {
        const slug = `loyalty/store/${storeId}/coupon/exchange`;
        const url = this.apiRequestService.requestUrl(slug, languageId, null, true, '1');

        return this.http.post<ApiFeelRedeemedCoupon>(url, { couponId });
    }

    /**
     * Función para canjear vales
     */
    public redeemVouchers(storeId: string, languageId: string, voucherId: string):
    Observable<ApiFeelRedeemedVoucher> {
        const slug = `loyalty/store/${storeId}/voucher/exchange`;
        const url = this.apiRequestService.requestUrl(slug, languageId, null, true, '1');

        return this.http.post<ApiFeelRedeemedVoucher>(url, { voucherId });
    }

    /**
     * Función para realizar la operación de Exchange de un regalo
     */
    public exchangeGift(storeId: string, languageId: string, giftId: string): Observable<ApiExchangedGift> {
        const slug = `loyalty/store/${storeId}/gifts/exchange`;
        const url = this.apiRequestService.requestUrl(slug, languageId, null, true, '1');

        return this.http.post<ApiExchangedGift>(url, { giftId })
            .pipe(catchError((error: unknown) => throwError(() => error)));
    }

    /**
     * Función para realizar la operación de Redeem de un regalo
     */
    public redeemGift(storeId: string, languageId: string, gift: ApiFeelGiftToRedeem): Observable<ApiRedeemedGift> {
        const slug = `loyalty/store/${storeId}/gifts/redemption`;
        const url = this.apiRequestService.requestUrl(slug, languageId, null, true, '1');

        return this.http.post<ApiRedeemedGift>(url, gift).pipe(catchError((error: unknown) => throwError(() => error)));
    }

    // TODO ECOMDUTI-20993 [TechDebt] - cambiar a que devuelva un observable con algo
    public setAdditionalData(data: any, storeId: string, languageId: string) {
        const slug = `loyalty/store/${storeId}/customer/additional-data`;
        const url = this.apiRequestService.requestUrl(slug, languageId, null, true, '1');

        return this.http.post<any>(url, {
            preferCategories: data?.preferCategories,
            preferStyles: data?.preferStyles,
            occupation: data?.occupation,
        });
    }

    public getAdditionalData(storeId: string, languageId: string): Observable<ApiFeelAdditionalData> {
        const slug = `loyalty/store/${storeId}/customer/additional-data`;
        const url = this.apiRequestService.requestUrl(
            slug,
            languageId,
            [
                { key: 'additionalFields', value: 'occupation' },
                { key: 'additionalFields', value: 'preferStyles' },
                { key: 'additionalFields', value: 'preferCategories' },
            ],
            true,
            '1'
        );

        return this.http.get<ApiFeelAdditionalData>(url);
    }
}
